import { ComponentProps } from 'react';
import { css, styled } from 'styled-components';
import { ColorName } from '../../styles/colors.ts';
import { fallbackFontFamilies } from '../../styles/consts.ts';
import { resetButtonStyles } from '../../styles/helpers.ts';
import { LoadingSpinner } from '../spinners';

export type MobileButtonProps = WrapperProps & {
  loading?: boolean;
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  // Should be carefully replaced with bgColor
  color?: ColorName;
  textColor?: ColorName;
};

const MobileButton = ({
  children,
  loading = false,
  disabled: disabledProp,
  size = 'normal',
  variant = 'contained',
  fullWidth = false,
  color,
  textColor,
  ...props
}: MobileButtonProps) => {
  const disabled = !!(loading || disabledProp);
  return (
    <Wrapper
      $size={size}
      $variant={variant}
      disabled={disabled}
      $fullWidth={fullWidth}
      $color={color}
      $textColor={textColor}
      {...props}
    >
      <Content $hidden={loading}>{children}</Content>
      {loading && (
        <Loader>
          <LoadingSpinner />
        </Loader>
      )}
    </Wrapper>
  );
};

type Variant = 'contained' | 'outlined';
type Size = 'normal' | 'medium' | 'small';

const Content = styled.span<{
  $hidden: boolean;
}>`
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  text-transform: uppercase;
`;

const stylesBySize: {
  [key in Size]: ReturnType<typeof css>;
} = {
  normal: css`
    min-height: 40px;
    padding: 4px 8px;
    border-radius: 20px;
    font-size: 16px;
    line-height: ${28 / 16};
  `,
  medium: css`
    min-width: 120px;
    min-height: 32px;
    padding: 4px 8px;
    border-radius: 30px;
    font-size: 16px;
    line-height: ${16 / 16};
  `,
  small: css`
    min-height: 24px;
    padding: 4px 8px;
    border-radius: 30px;
    font-size: 14px;
    line-height: ${14 / 14};
  `,
};

const stylesByVariant: {
  [key in Variant]: ReturnType<typeof css>;
} = {
  contained: css`
    background-color: ${({ theme }) => theme.getColor('accentSecondary')};
    color: ${({ theme }) =>
      theme.getColor(theme.greenTheme ? 'completelyBlack' : 'completelyWhite')};
  `,
  outlined: css`
    border: 1px solid ${({ theme }) => theme.colors.accentPrimary};
    background-color: transparent;
    color: ${({ theme }) => theme.getColor('accentSecondary')};
  `,
};

const disabledStyles = css`
  background-color: ${({ theme }) => theme.getColor('bgCards')};
  font-weight: 800;
  color: ${({ theme }) => theme.getColor('textDark')};
`;

type WrapperStyleProps = {
  $size: Size;
  $variant: Variant;
  $fullWidth: boolean;
  $color: ColorName | undefined;
  $textColor: ColorName | undefined;
};

type WrapperProps = Omit<ComponentProps<typeof Wrapper>, keyof WrapperStyleProps>;

const Wrapper = styled.button<WrapperStyleProps>`
  ${resetButtonStyles};
  font-family: Inter, ${fallbackFontFamilies};
  font-weight: 700;
  position: relative;
  ${({ $size }) => stylesBySize[$size]};
  ${({ $variant }) => stylesByVariant[$variant]};
  ${({ theme, $color }) =>
    !!$color &&
    css`
      background-color: ${theme.getColor($color)};
    `};
  ${({ theme, $textColor }) =>
    !!$textColor &&
    css`
      color: ${theme.getColor($textColor)};
    `};
  ${({ disabled }) => disabled && disabledStyles};
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `};
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

export { MobileButton };
